<template>
  <div class="accordion-section">
    <!-- セクションヘッダー（アコーディオンなし） -->
    <div class="section-header">
      <span class="section-title">{{ title }}</span>
    </div>

    <!-- セクションコンテンツ（グリッド形式） -->
    <div class="grid-container">
      <div
        v-for="(itemData, itemIndex) in items"
        :key="itemIndex"
        class="item-wrapper"
      >
        <!-- 子要素がある場合: Vuexyのアコーディオンで表示 -->
        <vs-collapse
          v-if="itemData.children && itemData.children.length > 0"
          accordion
          class="nested-accordion"
        >
          <vs-collapse-item not-arrow>
            <template #header>
              <div class="item-row has-children">
                <div class="item-text">{{ itemData.label }}</div>

                <!-- プラスアイコン -->
                <feather-icon icon="PlusIcon" class="plus-icon" />

                <!-- マイナスアイコン -->
                <feather-icon icon="MinusIcon" class="minus-icon" />
              </div>
            </template>

            <!-- 子要素のリスト -->
            <div class="children-list">
              <div
                v-for="(child, childIndex) in itemData.children"
                :key="childIndex"
                class="child-item"
                @click="handleChildClick(child, itemData)"
              >
                <div class="child-text">{{ child.label }}</div>
                <feather-icon icon="ChevronRightIcon" svgClasses="h-6 w-6" />
              </div>
            </div>
          </vs-collapse-item>
        </vs-collapse>

        <!-- 子要素がない場合: 通常のクリック可能な行 -->
        <div
          v-else
          class="item-row no-children"
          @click="handleItemClick(itemData)"
        >
          <div class="item-text">{{ itemData.label }}</div>
          <feather-icon icon="ChevronRightIcon" svgClasses="h-6 w-6" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AccordionData",
  props: {
    /**
     * セクションのタイトル
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * 表示するアイテムの配列
     *
     * データ形式：
     * {
     *   label: string,           // 表示ラベル
     *   children?: Array,        // 子要素（アコーディオン展開用）
     *   onClick?: Function       // クリック時のコールバック
     * }
     */
    items: {
      type: Array,
      required: true,
      default: () => [],
    },
  },

  methods: {
    /**
     * 子要素がない項目のクリックハンドラ
     * @param {Object} itemData - クリックされた項目のデータ
     */
    handleItemClick(itemData) {
      const section = { title: this.title };

      // データ内のonClick関数を実行
      if (itemData.onClick) {
        itemData.onClick(itemData, section);
      }
    },
    /**
     * 子要素のクリックハンドラ
     * @param {Object} child - クリックされた子要素のデータ
     * @param {Object} parent - 親要素のデータ
     */
    handleChildClick(child, parent) {
      const section = { title: this.title };

      // 子要素のonClick関数を実行
      if (child.onClick) {
        child.onClick(child, parent, section);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

// Vuexyのアコーディオンのデフォルトスタイルをリセット
.vs-collapse.nested-accordion.default {
  all: unset;
}

.plus-icon,
.minus-icon {
  position: absolute;
  right: 8px;
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.plus-icon {
  opacity: 1;
  transform: scale(1);
}

.minus-icon {
  opacity: 0;
  transform: scale(0.5);
}

/* open-item の時に反転 */
::v-deep .open-item .plus-icon {
  opacity: 0;
  transform: scale(0.5);
}

::v-deep .open-item .minus-icon {
  opacity: 1;
  transform: scale(1);
}

// アコーディオンセクション全体のコンテナ
.accordion-section {
  display: flex;
  flex-direction: column;
  gap: 8px;

  // セクションヘッダー（タイトル部分）
  .section-header {
    display: flex;
    align-items: center;
    min-height: 36px;
    width: 100%;
    padding: 0 4px;

    .section-title {
      font-family: $aisea-font-family;
      font-size: 14x;
      font-weight: 700;
      line-height: 20px;
      color: $aisea-dark-gray;
      word-break: break-word;
      overflow-wrap: break-word;
    }
  }

  // グリッドコンテナ（画面幅に応じて自動レイアウト）
  .grid-container {
    display: flex;
    flex-wrap: wrap;
    border: 1px solid $aisea-light-white;
    border-radius: 6px;
    padding: 8px;

    // アイテムラッパー
    .item-wrapper {
      flex: 1 1 320px; // 320pxを下限にしつつ横幅いっぱいを活用

      // ネストされたアコーディオン（子要素がある項目）
      .nested-accordion {
        ::v-deep .vs-collapse-item {
          .vs-collapse-item--header {
            padding: 0;

            &:hover {
              background-color: rgba($aisea-light-white, 0.3);
            }
          }

          .vs-collapse-item--content {
            .con-content--item {
              padding: 0;
            }
          }
        }
      }

      // 項目の行（親項目または子要素なしの項目）
      .item-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding: 8px 8px 8px 14px;
        padding: 0 8px;
        transition: background-color 0.2s ease;

        // 子要素がない項目（クリック可能）
        &.no-children {
          cursor: pointer;

          &:hover {
            background-color: rgba($aisea-light-white, 0.3);
          }

          .item-text {
            font-family: $aisea-font-family;
            font-size: 16px;
            font-weight: 400;
            line-height: 22px;
            color: $aisea-black;
          }
        }

        // 子要素がある項目（アコーディオンのヘッダー）
        &.has-children {
          .item-text {
            font-family: $aisea-font-family;
            font-size: 16px;
            font-weight: 400;
            line-height: 22px;
            color: $aisea-black;
          }
        }

        // 項目のテキスト（共通スタイル）
        .item-text {
          flex: 1;
          word-break: break-word;
          overflow-wrap: break-word;
        }
      }

      // 子要素のリスト
      .children-list {
        border-top: 1px solid $aisea-light-white;
        // 子要素のアイテム
        .child-item {
          display: flex;
          min-height: 36px;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          padding: 8px 8px 8px 24px;
          transition: background-color 0.2s ease;

          &:hover {
            background-color: rgba($aisea-light-white, 0.3);
          }

          // 子要素のテキスト
          .child-text {
            font-family: $aisea-font-family;
            font-size: 16px;
            font-weight: 400;
            line-height: 22px;
            color: $aisea-black;
            flex: 1;
            word-break: break-word;
            overflow-wrap: break-word;
          }
        }
      }
    }
  }
}
</style>
